import { useState, useEffect, useRef } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { MessageSquare, Send, X, ChevronRight, Clock, CheckCircle, AlertTriangle, User, Shield } from 'lucide-react';

const STATUS_CONFIG = {
  open:        { label: 'Open',        color: 'bg-red-100 text-red-700',    dot: 'bg-red-500' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700',  dot: 'bg-blue-500' },
  resolved:    { label: 'Resolved',    color: 'bg-green-100 text-green-700',dot: 'bg-green-500' },
  closed:      { label: 'Closed',      color: 'bg-gray-100 text-gray-500',  dot: 'bg-gray-400' },
};
const PRIORITY_COLORS = {
  urgent: 'text-red-600 font-bold', high: 'text-orange-600 font-semibold',
  medium: 'text-amber-600', low: 'text-gray-400',
};

function fmt(d) {
  if (!d) return '';
  try {
    const date = new Date(d);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
    return date.toLocaleDateString('en-IN',{day:'2-digit',month:'short'});
  } catch { return ''; }
}

export default function ClientTicketsPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('client_support');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const msgEndRef = useRef(null);

  const loadList = () => {
    setLoading(true);
    api.get('/admin/tickets', { params: filterStatus ? { status: filterStatus } : {} })
      .then(r => setTickets(r.data))
      .catch(() => toast.error('Load failed'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadList(); }, [filterStatus]);

  const openTicket = async (id) => {
    setActiveId(id);
    setMsgLoading(true);
    try {
      const r = await api.get(`/admin/tickets/${id}`);
      setActive(r.data);
      // update unread count in list
      setTickets(ts => ts.map(t => t.id===id ? {...t, unread_client:0} : t));
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Failed to load'); }
    finally { setMsgLoading(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeId) return;
    setSending(true);
    try {
      await api.post(`/admin/tickets/${activeId}/messages`, { message: reply.trim() });
      setReply('');
      const r = await api.get(`/admin/tickets/${activeId}`);
      setActive(r.data);
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch { toast.error('Send failed'); }
    finally { setSending(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/tickets/${id}/status`, { status });
      setTickets(ts => ts.map(t => t.id===id ? {...t, status} : t));
      if (active?.id === id) setActive(a => ({...a, status}));
      toast.success('Status updated');
    } catch { toast.error('Failed'); }
  };

  const totalUnread = tickets.reduce((s,t) => s + (t.unread_client||0), 0);

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black text-gray-900">Client Support</h1>
            {totalUnread > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                {totalUnread} new
              </span>
            )}
          </div>
          <p className="text-gray-400 text-sm mt-0.5">Chat-based support tickets from clients</p>
        </div>
        <div className="flex gap-2">
          {Object.entries(STATUS_CONFIG).map(([k,v]) => (
            <button key={k}
              onClick={() => setFilterStatus(filterStatus===k ? '' : k)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${filterStatus===k ? v.color+' ring-2 ring-offset-1 ring-current' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{height:'calc(100vh - 200px)', minHeight:500}}>
        {/* Ticket List */}
        <div className="card overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-600">{tickets.length} tickets</p>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-6 h-6 border-3 border-amber-500 border-t-transparent rounded-full" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="py-12 text-center text-gray-400 text-sm">No tickets yet</div>
            ) : tickets.map(t => {
              const sc = STATUS_CONFIG[t.status] || STATUS_CONFIG.open;
              const isActive = activeId === t.id;
              return (
                <div key={t.id}
                  onClick={() => openTicket(t.id)}
                  className={`px-4 py-3 cursor-pointer transition-colors ${isActive ? 'bg-amber-50 border-l-2 border-amber-400' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-semibold text-gray-900 leading-tight truncate">{t.subject}</p>
                    {t.unread_client > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full flex-shrink-0">{t.unread_client}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500 font-medium">{t.client_name}</span>
                    {t.project_name && <span className="text-xs text-gray-400">· {t.project_name}</span>}
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${sc.color}`}>{sc.label}</span>
                    <span className="text-xs text-gray-400">{fmt(t.updated_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Chat Area */}
        <div className="lg:col-span-2 card overflow-hidden flex flex-col">
          {!activeId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 font-medium">Select a ticket to view conversation</p>
              </div>
            </div>
          ) : msgLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
            </div>
          ) : active && (
            <>
              {/* Ticket header */}
              <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 text-sm leading-tight">{active.subject}</h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs text-gray-500">{active.client_name}</span>
                    {active.project_name && <span className="text-xs text-gray-400">· {active.project_name}</span>}
                    <span className={`text-xs font-semibold ${PRIORITY_COLORS[active.priority]}`}>{active.priority}</span>
                  </div>
                </div>
                <select
                  value={active.status}
                  onChange={e => updateStatus(active.id, e.target.value)}
                  className="field-input flex-shrink-0" style={{width:'auto', fontSize:12}}>
                  {Object.entries(STATUS_CONFIG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {active.messages?.map(m => {
                  const isAdmin = m.sender_role === 'admin';
                  return (
                    <div key={m.id} className={`flex items-end gap-2 ${isAdmin ? 'flex-row-reverse' : 'flex-row'}`}>
                      {/* Avatar */}
                      <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold ${isAdmin ? 'bg-amber-500' : 'bg-blue-500'}`}>
                        {isAdmin ? <Shield size={13}/> : <User size={13}/>}
                      </div>
                      {/* Bubble */}
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        isAdmin
                          ? 'bg-amber-500 text-white rounded-br-sm'
                          : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                      }`}>
                        <p style={{whiteSpace:'pre-wrap'}}>{m.message}</p>
                        <p className={`text-xs mt-1 ${isAdmin ? 'text-amber-100' : 'text-gray-400'}`}>{fmt(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={msgEndRef} />
              </div>

              {/* Reply box */}
              <div className="p-4 border-t border-gray-100">
                {active.status === 'closed' ? (
                  <div className="text-center py-3 text-gray-400 text-sm">This ticket is closed</div>
                ) : (
                  <div className="flex gap-2">
                    <textarea
                      value={reply}
                      onChange={e => setReply(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }}}
                      placeholder="Type reply… (Enter to send, Shift+Enter for new line)"
                      className="field-input flex-1 resize-none"
                      rows={2}
                    />
                    <button
                      onClick={sendReply}
                      disabled={!reply.trim() || sending}
                      className="btn-primary flex-shrink-0 self-end"
                      style={{padding:'10px 16px'}}
                    >
                      <Send size={16}/>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  
    </PermissionGuard>
  );
}
